import React, { useEffect, useState } from "react";
import { Plus, Edit3, Trash2, X, KeyRound, Store, ShieldCheck, Mail } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

const MODULE_KEYS = ["employees", "licenses", "dayoff", "empmgmt"];
const PERM_GROUPS = {
  employees: ["emp_view", "emp_add", "emp_edit", "emp_certificates", "emp_attachments", "emp_export"],
  licenses: ["lic_view", "lic_edit", "lic_attachments"],
  dayoff: ["dayoff_view", "dayoff_edit", "dayoff_confirm"],
  empmgmt: ["em_view", "em_add", "em_edit", "em_transfer", "em_vacation", "em_documents", "em_reports"],
};

export const BranchManagers = () => {
  const { t } = useT();
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [areaManagers, setAreaManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | bm row
  const [form, setForm] = useState({});
  const [perms, setPerms] = useState({});
  const [modules, setModules] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [credFor, setCredFor] = useState(null);
  const [credForm, setCredForm] = useState({ username: "", password: "" });
  const [credErr, setCredErr] = useState("");
  const [pending, setPending] = useState([]);
  const [pickEmp, setPickEmp] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [bm, br, pd] = await Promise.all([
        api.get("/branch-managers"), api.get("/branches"),
        api.get("/branch-managers/pending-premium").catch(() => ({ data: [] })),
      ]);
      setItems(bm.data || []);
      setBranches(br.data || []);
      setPending(pd.data || []);
      if (isSuper) {
        const am = await api.get("/area-managers").catch(() => ({ data: [] }));
        setAreaManagers(am.data || []);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const premiumBranches = (amId) => branches.filter((b) =>
    (b.branch_type || "").toLowerCase().includes("premium") && !b.is_vacation && !b.is_admin &&
    (!isSuper || !amId || b.area_manager_id === amId));

  const openNew = () => {
    setForm({ name: "", username: "", password: "", email: "", phone: "", branch_id: "", area_manager_id: "" });
    setPerms({}); setModules({}); setErr(""); setEditing({});
  };
  const openEdit = (bm) => {
    setForm({ name: bm.full_name || "", email: bm.email || "", phone: bm.phone || "", branch_id: bm.branch_id || "", is_active: bm.is_active !== false });
    setPerms(bm.permissions || {}); setModules(bm.modules || {}); setErr(""); setEditing(bm);
  };
  const close = () => setEditing(null);

  const save = async () => {
    setErr(""); setBusy(true);
    try {
      if (editing.id) {
        await api.patch(`/branch-managers/${editing.id}`, {
          name: form.name, email: form.email, phone: form.phone, branch_id: form.branch_id || undefined,
          is_active: form.is_active, permissions: perms, modules,
        });
      } else {
        await api.post("/branch-managers", { ...form, permissions: perms, modules });
      }
      close(); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === "string" ? d : t("save_failed"));
    } finally { setBusy(false); }
  };

  const remove = async (bm) => {
    if (!window.confirm(t("bm_delete_confirm", { name: bm.full_name || bm.username }))) return;
    await api.delete(`/branch-managers/${bm.id}`);
    load();
  };

  const resetOtp = async (bm) => {
    if (!window.confirm(t("ss_otp_confirm", { name: bm.full_name || bm.username }))) return;
    try {
      const r = await api.post(`/users/${bm.id}/reset-otp`);
      const chs = (r.data.channels || []).map((c) => t(`ss_ch_${c}`)).join("، ");
      setFeedback({ kind: "ok", text: t("ss_otp_sent", { channels: chs }) });
    } catch (e) {
      const d = e?.response?.data?.detail;
      setFeedback({ kind: "error", text: typeof d === "string" ? d : t("save_failed") });
    }
  };

  const assignFromEmployee = async (branchId) => {
    const empId = pickEmp[branchId];
    if (!empId) return;
    try {
      await api.post("/branch-managers/assign-from-employee", { branch_id: branchId, employee_id: empId });
      setFeedback({ kind: "ok", text: t("bm_assigned_ok") });
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      setFeedback({ kind: "error", text: typeof d === "string" ? d : t("save_failed") });
    }
  };

  const ignorePremium = async (branchId) => {
    await api.post("/branch-managers/ignore-premium", { branch_id: branchId }).catch(() => {});
    setPending((l) => l.filter((p) => p.branch_id !== branchId));
  };

  const saveCreds = async () => {
    setCredErr(""); setBusy(true);
    try {
      await api.post(`/branch-managers/${credFor.id}/credentials`, credForm);
      setCredFor(null); load();
      setFeedback({ kind: "ok", text: t("bm_creds_saved") });
    } catch (e) {
      const d = e.response?.data?.detail;
      setCredErr(typeof d === "string" ? d : t("save_failed"));
    } finally { setBusy(false); }
  };

  const toggleModule = (m) => {
    const on = !modules[m];
    setModules({ ...modules, [m]: on });
    if (!on) {
      const cleared = { ...perms };
      (PERM_GROUPS[m] || []).forEach((k) => { cleared[k] = false; });
      setPerms(cleared);
    }
  };

  return (
    <div className="space-y-4" data-testid="branch-managers-page">
      <div className="flex items-center justify-between">
        <p className="text-gray-500 text-sm">{t("bm_subtitle")}</p>
        <button className="btn btn-primary" onClick={openNew} data-testid="add-bm-button">
          <Plus size={16} /> {t("bm_add")}
        </button>
      </div>

      {feedback && (
        <div className={`rounded-lg p-2.5 text-sm border ${feedback.kind === "ok" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`}
          data-testid="bm-feedback" onClick={() => setFeedback(null)}>
          {feedback.text}
        </div>
      )}

      {pending.length > 0 && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 space-y-3" data-testid="bm-pending-panel">
          <div className="font-black text-amber-900 text-sm">⚠️ {t("bm_pending_title")} ({pending.length})</div>
          <p className="text-xs text-amber-800">{t("bm_pending_hint")}</p>
          {pending.map((p) => (
            <div key={p.branch_id} className="bg-white rounded-xl border border-amber-200 p-3 flex flex-wrap items-center gap-3" data-testid={`bm-pending-${p.code}`}>
              <div className="min-w-0">
                <div className="font-bold text-slate-800 text-sm">{p.code ? `${p.code} · ` : ""}{p.name}</div>
                {p.has_supervisor && <div className="text-[11px] text-amber-700">{t("bm_pending_has_sup")}</div>}
              </div>
              <select className="inp flex-1 min-w-[220px]" value={pickEmp[p.branch_id] || ""}
                onChange={(e) => setPickEmp({ ...pickEmp, [p.branch_id]: e.target.value })}
                data-testid={`bm-pending-select-${p.code}`}>
                <option value="">{t("bm_pending_pick")}</option>
                {p.employees.map((e2) => (
                  <option key={e2.id} value={e2.id}>{e2.name}{e2.employee_id ? ` — ${e2.employee_id}` : ""}{e2.position ? ` (${e2.position})` : ""}</option>
                ))}
              </select>
              <div className="flex gap-2">
                <button className="btn btn-primary" disabled={!pickEmp[p.branch_id]} onClick={() => assignFromEmployee(p.branch_id)} data-testid={`bm-pending-assign-${p.code}`}>
                  {t("bm_pending_assign")}
                </button>
                <button className="btn btn-secondary" onClick={() => ignorePremium(p.branch_id)} data-testid={`bm-pending-ignore-${p.code}`}>
                  {t("bm_pending_ignore")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-center text-gray-400 py-10">…</div>
      ) : items.length === 0 ? (
        <div className="card p-8 text-center text-gray-400" data-testid="bm-empty">
          <Store size={32} className="mx-auto mb-2 opacity-40" />
          {t("bm_empty")}
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr>
                <th>{t("bm_name")}</th>
                <th>{t("username")}</th>
                <th>{t("bm_branch")}</th>
                <th>{t("bm_modules")}</th>
                <th>{t("status")}</th>
                <th className="text-left">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((bm) => (
                <tr key={bm.id} data-testid={`bm-row-${bm.id}`}>
                  <td className="font-semibold">{bm.full_name}</td>
                  <td>
                    {bm.has_login
                      ? <span className="text-gray-500" style={{ direction: "ltr", display: "inline-block" }}>{bm.username}</span>
                      : <button className="btn btn-ghost text-amber-700" onClick={() => { setCredForm({ username: "", password: "" }); setCredErr(""); setCredFor(bm); }} data-testid={`bm-create-login-${bm.id}`}>
                          <KeyRound size={13} /> {t("bm_no_login")}
                        </button>}
                  </td>
                  <td>
                    <span className="pill bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 text-[11px]">
                      {bm.branch_code ? `${bm.branch_code} · ` : ""}{bm.branch_name || "—"}
                    </span>
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {MODULE_KEYS.filter((m) => bm.modules?.[m]).map((m) => (
                        <span key={m} className="pill bg-teal-50 text-teal-700 border border-teal-200 text-[10px]">{t(`bm_mod_${m}`)}</span>
                      ))}
                      {!MODULE_KEYS.some((m) => bm.modules?.[m]) && <span className="text-gray-300 text-xs">—</span>}
                    </div>
                  </td>
                  <td>
                    {bm.is_active !== false
                      ? <span className="pill bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px]">{t("active")}</span>
                      : <span className="pill bg-rose-50 text-rose-700 border border-rose-200 text-[11px]">{t("inactive")}</span>}
                  </td>
                  <td className="text-left whitespace-nowrap">
                    <button className="btn btn-ghost" onClick={() => openEdit(bm)} data-testid={`edit-bm-${bm.id}`}>
                      <Edit3 size={14} /> {t("edit")}
                    </button>
                    <button className="btn btn-ghost" onClick={() => resetOtp(bm)} title={t("ss_reset_btn")} data-testid={`reset-bm-${bm.id}`}>
                      <KeyRound size={14} />
                    </button>
                    <button className="btn btn-danger" onClick={() => remove(bm)} data-testid={`delete-bm-${bm.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {credFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop animate-fade-in" onClick={() => setCredFor(null)}>
          <div className="bg-white rounded-2xl card-shadow w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-teal-900 mb-1">{t("bm_creds_title")}</h3>
            <p className="text-sm text-gray-500 mb-3">{credFor.full_name} — {credFor.branch_name}</p>
            {credErr && <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-lg p-2.5 text-sm mb-2" data-testid="bm-creds-error">⚠️ {credErr}</div>}
            <div className="space-y-3">
              <div>
                <label className="inp-label">{t("username")} *</label>
                <input className="inp" style={{ direction: "ltr" }} value={credForm.username} onChange={(e) => setCredForm({ ...credForm, username: e.target.value })} data-testid="bm-creds-username" />
              </div>
              <div>
                <label className="inp-label">{t("password")} *</label>
                <input className="inp" style={{ direction: "ltr" }} type="text" value={credForm.password} onChange={(e) => setCredForm({ ...credForm, password: e.target.value })} data-testid="bm-creds-password" />
              </div>
              <div className="flex justify-end gap-2">
                <button className="btn btn-secondary" onClick={() => setCredFor(null)}>{t("cancel")}</button>
                <button className="btn btn-primary" disabled={busy || !credForm.username.trim() || !credForm.password} onClick={saveCreds} data-testid="bm-creds-save">
                  {busy ? t("saving") : t("save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop animate-fade-in" onClick={close}>
          <div className="bg-white rounded-2xl card-shadow w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-teal-900">{editing.id ? t("bm_edit_title") : t("bm_add")}</h3>
              <button className="btn btn-ghost" onClick={close}><X size={16} /></button>
            </div>
            <div className="space-y-4">
              {err && <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-lg p-2.5 text-sm" data-testid="bm-error">⚠️ {err}</div>}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="inp-label">{t("bm_name")} *</label>
                  <input className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="bm-name-input" />
                </div>
                {!editing.id && (
                  <>
                    <div>
                      <label className="inp-label">{t("username")} *</label>
                      <input className="inp" style={{ direction: "ltr" }} value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="bm-username-input" />
                    </div>
                    <div>
                      <label className="inp-label">{t("password")} *</label>
                      <input className="inp" style={{ direction: "ltr" }} type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="bm-password-input" />
                    </div>
                  </>
                )}
                <div>
                  <label className="inp-label flex items-center gap-1"><Mail size={11} /> {t("email_optional")}</label>
                  <input className="inp" style={{ direction: "ltr" }} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="bm-email-input" />
                </div>
                <div>
                  <label className="inp-label">{t("phone")}</label>
                  <input className="inp" style={{ direction: "ltr" }} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="bm-phone-input" />
                </div>
                {isSuper && !editing.id && (
                  <div>
                    <label className="inp-label">{t("bm_area_manager")} *</label>
                    <select className="inp" value={form.area_manager_id} onChange={(e) => setForm({ ...form, area_manager_id: e.target.value, branch_id: "" })} data-testid="bm-am-select">
                      <option value="">—</option>
                      {areaManagers.map((a) => <option key={a.id} value={a.id}>{a.full_name || a.username}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="inp-label flex items-center gap-1"><Store size={11} /> {t("bm_branch")} (Premium) *</label>
                  <select className="inp" value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })} data-testid="bm-branch-select">
                    <option value="">—</option>
                    {premiumBranches(isSuper ? (editing.id ? editing.area_manager_id : form.area_manager_id) : null).map((b) => (
                      <option key={b.id} value={b.id}>{b.code ? `${b.code} · ` : ""}{b.name}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-gray-400 mt-1">{t("bm_branch_hint")}</p>
                </div>
                {editing.id && (
                  <div className="flex items-center gap-2 mt-5">
                    <input type="checkbox" id="bm-active" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="bm-active-toggle" />
                    <label htmlFor="bm-active" className="text-sm font-semibold text-slate-700">{t("active")}</label>
                  </div>
                )}
              </div>

              <div className="border-t border-slate-100 pt-3">
                <div className="flex items-center gap-2 mb-2 text-teal-900 font-bold text-sm">
                  <ShieldCheck size={15} /> {t("bm_perms_title")}
                </div>
                <p className="text-[11px] text-gray-400 mb-3">{t("bm_perms_hint")}</p>
                <div className="space-y-3">
                  {MODULE_KEYS.map((m) => (
                    <div key={m} className={`rounded-xl border p-3 ${modules[m] ? "border-teal-200 bg-teal-50/40" : "border-slate-200 bg-slate-50/60"}`} data-testid={`bm-module-${m}`}>
                      <label className="flex items-center gap-2 font-bold text-sm text-slate-800 cursor-pointer">
                        <input type="checkbox" checked={!!modules[m]} onChange={() => toggleModule(m)} data-testid={`bm-module-toggle-${m}`} />
                        {t(`bm_mod_${m}`)}
                      </label>
                      {modules[m] && (
                        <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-2 ps-6">
                          {PERM_GROUPS[m].map((k) => (
                            <label key={k} className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                              <input type="checkbox" checked={!!perms[k]} onChange={(e) => setPerms({ ...perms, [k]: e.target.checked })} data-testid={`bm-perm-${k}`} />
                              {t(`bm_perm_${k}`)}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer px-1">
                    <input type="checkbox" checked={!!perms.notif_view} onChange={(e) => setPerms({ ...perms, notif_view: e.target.checked })} data-testid="bm-perm-notif_view" />
                    {t("bm_perm_notif_view")}
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button className="btn btn-secondary" onClick={close}>{t("cancel")}</button>
                <button className="btn btn-primary" disabled={busy || !form.name?.trim() || !form.branch_id || (!editing.id && (!form.username?.trim() || !form.password))} onClick={save} data-testid="bm-save-button">
                  {busy ? t("saving") : t("save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BranchManagers;
